/**
 * BookingReports — key metrics with no-show rate, revenue, top services,
 * busiest hours, top clients. Reads /api/booking/reports/summary.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Calendar, DollarSign, TrendingDown, Users, Activity } from 'lucide-react';

export default function BookingReports({ api, auth }) {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    axios.get(`${api}/api/booking/reports/summary?days=${days}`, auth())
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, [api, days]);

  if (!data) return <p className="text-sm text-gray-medium">Loading reports…</p>;
  const t = data.totals;

  const kpiCards = [
    { label: 'Total Bookings', value: t.bookings, icon: Calendar, color: 'text-emerald-600' },
    { label: 'Revenue', value: `$${t.revenue.toLocaleString()}`, icon: DollarSign, color: 'text-emerald-600' },
    { label: 'No-Show Rate', value: `${t.no_show_rate}%`, icon: TrendingDown, color: t.no_show_rate > 15 ? 'text-red-600' : 'text-amber-600' },
    { label: 'Cancellation Rate', value: `${t.cancellation_rate}%`, icon: Activity, color: t.cancellation_rate > 20 ? 'text-red-600' : 'text-blue-600' },
  ];

  return (
    <div className="space-y-6" data-testid="booking-reports">
      <div className="flex gap-2">
        {[7, 30, 90, 180, 365].map((d) => (
          <button
            key={d}
            onClick={() => setDays(d)}
            className={`text-xs px-3 py-1.5 rounded-full border ${days === d ? 'bg-emerald-brand text-white border-emerald-brand' : 'bg-white border-gray-200'}`}
            data-testid={`report-days-${d}`}
          >Last {d}d</button>
        ))}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {kpiCards.map((k) => (
          <Card key={k.label}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-xs text-gray-medium mb-1">
                <k.icon className={`h-4 w-4 ${k.color}`} /> {k.label}
              </div>
              <div className={`text-2xl font-bold ${k.color}`}>{k.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle className="text-base">Top Services</CardTitle></CardHeader>
          <CardContent>
            {data.by_service.length === 0 ? <p className="text-sm text-gray-medium">No data.</p> : (
              <div className="space-y-2">
                {data.by_service.map((s) => (
                  <div key={s._id || 'na'} className="flex items-center justify-between text-sm">
                    <span className="text-text-primary">{s._id || 'N/A'}</span>
                    <span className="text-gray-medium">{s.count} · ${(s.revenue || 0).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Busiest Hours</CardTitle></CardHeader>
          <CardContent>
            {data.by_hour.length === 0 ? <p className="text-sm text-gray-medium">No data.</p> : (
              <div className="space-y-1">
                {data.by_hour.map((h) => (
                  <div key={h._id} className="flex items-center gap-2 text-sm">
                    <div className="w-14 text-gray-500 text-xs">{h._id}:00</div>
                    <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
                      <div
                        className="bg-emerald-brand h-full"
                        style={{ width: `${Math.min(100, (h.count / Math.max(...data.by_hour.map((x) => x.count))) * 100)}%` }}
                      />
                    </div>
                    <div className="w-8 text-right text-xs text-gray-medium">{h.count}</div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Top Resources</CardTitle></CardHeader>
          <CardContent>
            {data.by_resource.length === 0 ? <p className="text-sm text-gray-medium">No data.</p> : (
              <div className="space-y-2">
                {data.by_resource.map((r) => (
                  <div key={r._id || 'na'} className="flex items-center justify-between text-sm">
                    <span className="text-text-primary">{r.label || 'Unassigned'}</span>
                    <span className="text-gray-medium">{r.count} bookings</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Users className="h-4 w-4" /> Top Clients</CardTitle></CardHeader>
          <CardContent>
            {data.top_clients.length === 0 ? <p className="text-sm text-gray-medium">No data.</p> : (
              <div className="space-y-2">
                {data.top_clients.map((c) => (
                  <div key={c._id || 'na'} className="flex items-center justify-between text-sm">
                    <div>
                      <div className="text-text-primary">{c.name || 'Unknown'}</div>
                      {c.email && <div className="text-xs text-gray-medium">{c.email}</div>}
                    </div>
                    <div className="text-gray-medium">{c.count} · ${(c.revenue || 0).toLocaleString()}</div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
